/**
 * RegisterSimulator — an in-app POS interface that mimics a Clover Register
 * for testing purposes. Every "checkout" hits our /simulate-order endpoint,
 * which runs receipts through the SAME mapping + decrement pipeline real
 * Clover sales use. So this is a full end-to-end confidence test WITHOUT
 * needing a physical Clover device or the Register app.
 *
 * Layout:
 *   +----------------------+----------------+
 *   |  Item grid (search)  |  Cart          |
 *   |  click to add        |  totals        |
 *   |                      |  payment       |
 *   |                      |  [Complete]    |
 *   +----------------------+----------------+
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Minus, X, ShoppingCart, CreditCard, Banknote, Wallet, CheckCircle2, ArrowRightLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currency = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function RegisterSimulator({ businessSlug, isDark, taxRate = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);          // [{clover_item_id, name, price_cents, qty}]
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [completing, setCompleting] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  // OFF by default → sales are treated as REAL and count in Dashboard,
  // Tax Monitor, and Transactions. Flip ON only when you're genuinely
  // testing something and don't want to pollute reports.
  const [testMode, setTestMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/b/${businessSlug}/clover/inventory`);
        setItems((r.data.items || []).filter((i) => !i.hidden));
      } catch {
        toast.error("Could not load inventory. Run Sync Now first.");
      }
      setLoading(false);
    })();
  }, [businessSlug]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items.slice(0, 60);
    const q = search.toLowerCase();
    return items.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.barcode || "").toLowerCase().includes(q)
    ).slice(0, 60);
  }, [items, search]);

  const addToCart = (item) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.clover_item_id === item.clover_item_id);
      if (existing) {
        return prev.map((c) =>
          c.clover_item_id === item.clover_item_id ? { ...c, qty: c.qty + 1 } : c,
        );
      }
      return [...prev, {
        clover_item_id: item.clover_item_id,
        name: item.name || "Item",
        price_cents: item.price_cents || 100,
      qty: 1,
      }];
    });
  };

  const updateQty = (id, delta) => {
    setCart((prev) =>
      prev.map((c) => c.clover_item_id === id ? { ...c, qty: Math.max(0, c.qty + delta) } : c)
          .filter((c) => c.qty > 0)
    );
  };

  const removeLine = (id) => setCart((prev) => prev.filter((c) => c.clover_item_id !== id));

  const subtotal = cart.reduce((s, c) => s + c.price_cents * c.qty, 0) / 100.0;
  const tax = subtotal * (Number(taxRate) || 0);
  const total = subtotal + tax;

  const complete = async () => {
    if (cart.length === 0) {
      toast.info("Add items to the cart first");
      return;
    }
    setCompleting(true);
    try {
      const r = await axios.post(
        `${API}/grocery/b/${businessSlug}/clover/simulate-order`,
        {
          lines: cart.map((c) => ({ clover_item_id: c.clover_item_id, quantity: c.qty })),
          payment_method: paymentMethod,
          test_mode: testMode,
        }
      );
      setLastReceipt({
        order_id: r.data.test_order_id,
        total: r.data.total,
        subtotal: r.data.subtotal,
        tax: r.data.tax,
        items_decremented: r.data.items_decremented,
        line_count: r.data.line_count,
        payment_method: r.data.payment_method,
        source: r.data.source,
        cart_snapshot: cart,
      });
      setCart([]);
      const modeLabel = r.data.source === "test_sale" ? " (test — excluded from reports)" : "";
      toast.success(
        `Sale complete · ${currency(r.data.total)} · ${r.data.items_decremented} items decremented${modeLabel}`,
        { duration: 6000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sale failed");
    }
    setCompleting(false);
  };

  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const heading = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const gridItemBg = isDark ? "bg-slate-900 border-slate-700 hover:bg-slate-700" : "bg-slate-50 border-slate-200 hover:bg-slate-100";

  return (
    <div className="space-y-3" data-testid="register-simulator-root">
      {/* Notice bar — mode-aware */}
      <div className={`px-3 py-2 rounded-lg text-xs flex items-center gap-2 border flex-wrap ${
        testMode
          ? (isDark ? "bg-amber-500/10 border-amber-500/30 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800")
          : (isDark ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-emerald-50 border-emerald-200 text-emerald-800")
      }`}>
        <ArrowRightLeft className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="flex-1 min-w-[220px]">
          {testMode ? (
            <>
              <strong>Test Mode ON.</strong> Sales are tagged <code className="font-mono">test_sale</code> and
              excluded from Dashboard / Tax Monitor / Transactions. Great for smoke tests — bad for actual bookkeeping.
            </>
          ) : (
            <>
              <strong>Live Register.</strong> Every sale you complete here counts in your Dashboard,
              Tax Monitor and Transactions — same pipeline real Clover sales use. Toggle Test Mode to test without polluting reports.
            </>
          )}
        </span>
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={testMode}
            onChange={(e) => setTestMode(e.target.checked)}
            className="w-3.5 h-3.5 accent-amber-500"
            data-testid="register-test-mode-toggle"
          />
          <span className="text-[11px] font-semibold uppercase tracking-wide">Test mode</span>
        </label>
      </div>

      {/* Last receipt banner */}
      {lastReceipt && (
        <Card className={cardBg} data-testid="register-last-receipt">
          <CardContent className="p-3 flex items-center gap-3 text-sm">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-600"}`}>
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className={`font-medium ${heading}`}>
                Sale complete · {currency(lastReceipt.total)}
                {lastReceipt.source === "test_sale" && (
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-mono uppercase ${isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700"}`}>
                    test
                  </span>
                )}
              </div>
              <div className={`text-xs ${sub}`}>
                {lastReceipt.line_count} line{lastReceipt.line_count === 1 ? "" : "s"} ·
                <span className="uppercase ml-1">{lastReceipt.payment_method}</span>
                {typeof lastReceipt.tax === "number" && lastReceipt.tax > 0 && <> · tax {currency(lastReceipt.tax)}</>}
                {lastReceipt.items_decremented > 0 ? ` · ${lastReceipt.items_decremented} stock decrement${lastReceipt.items_decremented === 1 ? "" : "s"}` : ""}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setLastReceipt(null)}>×</Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Item grid */}
        <div className="lg:col-span-2 space-y-3">
          <Card className={cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-base flex items-center gap-2 ${heading}`}>
                <Search className="w-4 h-4" /> Items
              </CardTitle>
              <Input
                placeholder="Search by name or barcode…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={isDark ? "bg-slate-900 border-slate-700 text-white" : ""}
                autoFocus
                data-testid="register-search"
              />
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className={`text-sm ${sub}`}>Loading items…</p>
              ) : filtered.length === 0 ? (
                <p className={`text-sm ${sub}`}>No items match. Try clearing search, or run Sync Now on the Inventory tab if the list is empty.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="register-item-grid">
                  {filtered.map((it) => (
                    <button
                      key={it.clover_item_id}
                      onClick={() => addToCart(it)}
                      className={`text-left p-2.5 rounded-md border transition-all ${gridItemBg}`}
                      data-testid={`register-item-${it.clover_item_id}`}
                    >
                      <div className={`text-xs font-medium truncate ${heading}`} title={it.name}>
                        {it.name || "Unnamed"}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-sm font-mono ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                          {currency((it.price_cents || 0) / 100)}
                        </span>
                        <span className={`text-[9px] font-mono ${sub}`}>
                          {it.stock_count ?? it.manual_stock_count ?? "—"}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cart */}
        <div className="space-y-3">
          <Card className={cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-base flex items-center gap-2 ${heading}`}>
                <ShoppingCart className="w-4 h-4" /> Cart
                {cart.length > 0 && (
                  <Badge className="bg-cyan-100 text-cyan-800 border border-cyan-300 hover:bg-cyan-100">
                    {cart.reduce((s, c) => s + c.qty, 0)}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cart.length === 0 ? (
                <p className={`text-sm text-center py-6 ${sub}`}>Tap items to add to cart</p>
              ) : (
                <div className="space-y-1.5" data-testid="register-cart-lines">
                  {cart.map((c) => (
                    <div
                      key={c.clover_item_id}
                      className={`flex items-center justify-between gap-2 text-sm py-1 border-b ${isDark ? "border-slate-700" : "border-slate-200"}`}
                      data-testid={`register-cart-line-${c.clover_item_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={`truncate ${heading}`}>{c.name}</div>
                        <div className={`text-[10px] font-mono ${sub}`}>{currency(c.price_cents / 100)} × {c.qty}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateQty(c.clover_item_id, -1)}>
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-5 text-center text-xs font-mono">{c.qty}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateQty(c.clover_item_id, 1)}>
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeLine(c.clover_item_id)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Totals */}
              <div className={`text-sm space-y-0.5 pt-2 border-t ${isDark ? "border-slate-700" : "border-slate-200"}`}>
                <div className="flex justify-between">
                  <span className={sub}>Subtotal</span>
                  <span className={`font-mono ${heading}`}>{currency(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={sub}>Tax ({((taxRate || 0) * 100).toFixed(2)}%)</span>
                  <span className={`font-mono ${heading}`}>{currency(tax)}</span>
                </div>
                <div className="flex justify-between text-base font-semibold">
                  <span className={heading}>Total</span>
                  <span className={`font-mono ${isDark ? "text-emerald-400" : "text-emerald-600"}`} data-testid="register-total">
                    {currency(total)}
                  </span>
                </div>
              </div>

              {/* Payment method */}
              <div>
                <p className={`text-xs mb-1.5 ${sub}`}>Payment method</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { k: "card", icon: CreditCard, label: "Card" },
                    { k: "cash", icon: Banknote, label: "Cash" },
                    { k: "ebt", icon: Wallet, label: "EBT" },
                  ].map((p) => (
                    <button
                      key={p.k}
                      onClick={() => setPaymentMethod(p.k)}
                      className={`flex flex-col items-center gap-1 py-2 rounded-md border text-xs transition-all ${
                        paymentMethod === p.k
                          ? (isDark ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-300" : "bg-cyan-50 border-cyan-500 text-cyan-700")
                          : gridItemBg
                      }`}
                      data-testid={`register-pay-${p.k}`}
                    >
                      <p.icon className="w-4 h-4" />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={complete}
                disabled={cart.length === 0 || completing}
                className="w-full h-11 text-base"
                data-testid="register-complete-btn"
              >
                {completing ? "Processing…" : `Complete · ${currency(total)}`}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
